import {
  LayoutDashboard, ConciergeBell, Calendar, Users, HeartPulse,
  CreditCard, Scissors, FileText, Receipt,
  BarChart2, Megaphone, Network, Share2,
  Settings, Handshake, Badge,
  ShieldCheck, ClipboardList, FileEdit, Plug, Star, Package, FileCheck,
  Hospital, Truck, Pill, BookOpen, KanbanSquare,
} from 'lucide-react';
import type { ElementType } from 'react';
import { ROL } from '@/store/auth.store';

export interface ModuleSection {
  to: string;
  label: string;
  icon: ElementType;
  roles: string[];
  end?: boolean;
}

export interface AppModule {
  id: string;
  label: string;
  icon: ElementType;
  color: string;
  sections: ModuleSection[];
  dividerBefore?: boolean;
}

const TODOS   = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.RECEPCIONISTA, ROL.ASISTENTE];
const ADMIN   = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN];
const CLINICO = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO, ROL.MEDICO_ADMIN];
// Recepcionista queda fuera del módulo de tareas por decisión de negocio.
const TAREAS  = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN, ROL.MEDICO, ROL.ASISTENTE];
const FRONT   = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN, ROL.RECEPCIONISTA, ROL.ASISTENTE];

export const MODULES: AppModule[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    color: '#0e85bf',   // azul primario (brand)
    sections: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: TODOS },
    ],
  },
  {
    id: 'recepcion',
    label: 'Recepción',
    icon: ConciergeBell,
    color: '#f59e0b',   // ámbar
    sections: [
      { to: '/recepcion', label: 'Recepción', icon: ConciergeBell, roles: FRONT },
    ],
  },
  {
    id: 'agenda',
    label: 'Agenda',
    icon: Calendar,
    color: '#7c3aed',   // violeta
    sections: [
      { to: '/agenda', label: 'Agenda', icon: Calendar, roles: TODOS },
    ],
  },
  {
    id: 'pacientes',
    label: 'Pacientes',
    icon: Users,
    color: '#059669',   // esmeralda
    sections: [
      { to: '/pacientes', label: 'Pacientes', icon: Users, roles: TODOS },
    ],
  },
  {
    id: 'tareas',
    label: 'Tareas',
    icon: KanbanSquare,
    color: '#0891b2',   // cyan oscuro
    sections: [
      { to: '/tareas', label: 'Tareas', icon: KanbanSquare, roles: TAREAS },
    ],
  },
  {
    id: 'tratamientos',
    label: 'Tratamientos',
    icon: HeartPulse,
    color: '#dc2626',   // rojo
    sections: [
      { to: '/tratamientos', label: 'Tratamientos', icon: HeartPulse, roles: CLINICO },
    ],
  },
  {
    id: 'vademecum',
    label: 'Vademecum Clínico',
    icon: Pill,
    color: '#7e22ce',   // morado
    sections: [
      { to: '/vademecum', label: 'Vademecum Clínico', icon: Pill, roles: CLINICO },
    ],
  },
  {
    id: 'finanzas',
    label: 'Finanzas',
    icon: CreditCard,
    color: '#0d9488',   // jade/teal
    sections: [
      { to: '/finanzas',               label: 'Historial de cobros', icon: CreditCard, roles: [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.RECEPCIONISTA], end: true },
      { to: '/finanzas/corte',         label: 'Corte de caja',       icon: Scissors,   roles: ADMIN },
      { to: '/finanzas/cuentas',       label: 'Cuentas por cobrar',  icon: FileText,   roles: ADMIN },
      { to: '/finanzas/presupuestos',  label: 'Presupuestos',        icon: ClipboardList, roles: ADMIN },
      { to: '/finanzas/proveedores',   label: 'Proveedores',         icon: Truck,      roles: ADMIN },
      { to: '/finanzas/fiscal',        label: 'Facturación',         icon: Receipt,    roles: ADMIN },
    ],
  },
  {
    id: 'reportes',
    label: 'Reportes',
    icon: BarChart2,
    color: '#4f46e5',   // índigo
    sections: [
      { to: '/reportes', label: 'Reportes', icon: BarChart2, roles: CLINICO },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: '#db2777',   // rosa
    sections: [
      { to: '/marketing',            label: 'Campañas',   icon: Megaphone, roles: CLINICO, end: true },
      { to: '/marketing/plantillas', label: 'Plantillas', icon: FileEdit,  roles: CLINICO },
    ],
  },
  {
    id: 'red',
    label: 'Red médica',
    icon: Network,
    color: '#06b6d4',   // cian
    sections: [
      { to: '/red/directorio', label: 'Directorio médico', icon: Network,   roles: CLINICO },
      { to: '/red/referidos',  label: 'Referidos',          icon: Share2,    roles: CLINICO },
      { to: '/red/mi-perfil',  label: 'Mi perfil público',  icon: Hospital,  roles: CLINICO },
    ],
  },
  {
    id: 'personal',
    label: 'Personal',
    icon: Badge,
    color: '#ea580c',   // naranja
    dividerBefore: true,
    sections: [
      { to: '/personal', label: 'Personal', icon: Badge, roles: ADMIN },
    ],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    icon: Settings,
    color: '#94a3b8',   // slate (visible en fondo oscuro)
    sections: [
      { to: '/configuracion',               label: 'Ajustes',            icon: Settings,      roles: CLINICO },
      { to: '/configuracion/productos',     label: 'Productos y servicios', icon: Package,    roles: ADMIN },
      { to: '/configuracion/plantillas',      label: 'Plantillas',         icon: FileEdit,      roles: ADMIN },
      { to: '/configuracion/plantillas-exploracion', label: 'Formularios clínicos', icon: ClipboardList, roles: ADMIN },
      { to: '/configuracion/consentimientos', label: 'Consentimientos',   icon: FileCheck,     roles: CLINICO },
      { to: '/configuracion/vademecum',     label: 'Vademecum General',  icon: BookOpen,      roles: CLINICO },
      { to: '/configuracion/suscripcion',   label: 'Suscripción',        icon: Star,          roles: ADMIN },
    ],
  },
];

/** Mapea cada ruta base → id de módulo */
export const ROUTE_MODULE_MAP: Record<string, string> = {
  '/dashboard':                       'dashboard',
  '/recepcion':                       'recepcion',
  '/agenda':                          'agenda',
  '/agenda/horarios':                 'configuracion',
  '/consultas':                       'agenda',
  '/pacientes':                       'pacientes',
  '/tareas':                          'tareas',
  '/tratamientos':                    'tratamientos',
  '/finanzas':                        'finanzas',
  '/finanzas/proveedores':            'finanzas',
  '/finanzas/presupuestos':           'finanzas',
  '/reportes':                        'reportes',
  '/marketing':                       'marketing',
  '/marketing/plantillas':            'marketing',
  '/marketing/plantillas/nueva':      'marketing',
  '/marketing/campanas/nueva':        'marketing',
  '/red':                             'red',
  '/red/directorio':                 'red',
  '/red/referidos':                  'red',
  '/red/mi-perfil':                  'red',
  '/personal':                        'personal',
  '/configuracion':                   'configuracion',
  '/clinica':                         'configuracion',
  '/configuracion/convenios':         'configuracion',
  '/configuracion/productos':         'configuracion',
  '/configuracion/suscripcion':       'configuracion',
  '/configuracion/plantillas':        'configuracion',
  '/configuracion/plantillas-exploracion': 'configuracion',
  '/configuracion/consentimientos':   'configuracion',
  '/configuracion/vademecum':         'configuracion',
  '/vademecum':                       'vademecum',
};
